/**
 * The files that carry proposal lines, and the dropped doors' lines in them (`./dropped-door.ts`).
 *
 * A dropped door's line proposes nothing: its «requested» is the need no size run could carry. Left
 * in the file's table it reads as a request the engine turned down, beside a size that is not a size,
 * and it is a quarter of the table on a production run. So **every file that carries proposal lines
 * leaves them out of the table of what was proposed**, and nothing is lost:
 *
 *   • **Excel** gains a sheet of their own, «Negozi scartati» — article, name, store, name, week and
 *     the need — so the two sheets together are the run's lines, and a total over the first is what
 *     the screen's served figures are. The sheet's name is the shortest that says it: Excel cuts a
 *     sheet name at 31 characters, and the reason it carries («serie di taglie non componibile») is a
 *     column of the sheet, in words.
 *   • **CSV** is one table, so it simply excludes them, and says how many: in each row's
 *     \`provenance_absent\` (\`dropped_door_lines_excluded=N\`), in the file's filters and in the
 *     citation of the measure — the existing place a file states what it left out. The Excel
 *     workbook's «Tecnico» sheet carries the same three.
 *
 * Pure: the sheet is built from lines, names and a translator already in hand.
 */

import type { ProposalSummary } from '@/api/generated';
import type { ExportCell, ExportColumn, ExportRequest, ProvenanceFilter, WorkbookSheet } from '@/export';
import type { DownloadLabelling } from '@/planning';

import { DROPPED_DOOR_KEY, splitDroppedDoors } from './dropped-door';
import type { CitedMeasure, Translate } from './export-provenance';
import { nameExportCells, nameExportColumns, type Named } from './run-names';

/** What a file leaves out: the dropped doors' lines, and the need they carry. */
export interface DroppedExclusion {
  /** Lines written to the sheet, whatever they asked for. */
  readonly lines: number;
  /** Σ their `requested_units`: the pairs no size run could carry. */
  readonly pairs: number;
}

export const NO_EXCLUSION: DroppedExclusion = { lines: 0, pairs: 0 };

export function exclusionOf(dropped: readonly Pick<ProposalSummary, 'requested_units'>[]): DroppedExclusion {
  return dropped.length === 0
    ? NO_EXCLUSION
    : { lines: dropped.length, pairs: dropped.reduce((sum, line) => sum + line.requested_units, 0) };
}

/** The perimeter's statement of the exclusion: a filter like any other, so a provenance sheet lists it. */
export const DROPPED_FILTER_FIELD = 'dropped_door_lines';

export function droppedFilters(exclusion: DroppedExclusion): readonly ProvenanceFilter[] {
  return exclusion.lines === 0 ? [] : [{ field: DROPPED_FILTER_FIELD, operator: 'excluded', value: String(exclusion.lines) }];
}

/** The code the CSV states it in, on every row's `provenance_absent`. */
export function droppedAbsentNotes(exclusion: DroppedExclusion): readonly string[] {
  return exclusion.lines === 0 ? [] : [`dropped_door_lines_excluded=${exclusion.lines}`];
}

/** The citation the workbook's «Tecnico» sheet reads in words: «N righe di negozi scartati escluse …». */
export function droppedMeasures(exclusion: DroppedExclusion, translate: Translate): readonly CitedMeasure[] {
  return exclusion.lines === 0
    ? []
    : [{
      measure: 'dropped_door_lines_excluded',
      definition: translate('inseason.droppedDoor.export.excluded', { lines: exclusion.lines, pairs: exclusion.pairs }),
    }];
}

// ── the sheet ────────────────────────────────────────────────────────────────

/** The sheet's title (an ICU key): ≤31 characters once translated, Excel's own limit. */
export const DROPPED_SHEET_TITLE_KEY = 'inseason.droppedDoor.export.sheet';

const [STORE_NAME, ARTICLE_NAME, STORE_NAME_REASON, ARTICLE_NAME_REASON] = nameExportColumns(['store', 'article']) as [
  ExportColumn, ExportColumn, ExportColumn, ExportColumn,
];

/** The dropped doors' sheet: who, where, when, and how much could not be served. */
export const DROPPED_SHEET_COLUMNS: readonly ExportColumn[] = [
  { key: 'article_key', headerKey: 'inseason.column.article', kind: 'text', freeze: true, descriptionKey: 'inseason.columnGuide.proposal.article_key' },
  ARTICLE_NAME,
  { key: 'store_key', headerKey: 'inseason.column.store', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.store_key' },
  STORE_NAME,
  { key: 'ship_week', headerKey: 'inseason.column.shipWeek', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.ship_week' },
  {
    key: 'unservable_units', headerKey: DROPPED_DOOR_KEY.need, kind: 'integer', unit: 'pairs', total: 'sum',
    descriptionKey: 'inseason.columnGuide.droppedDoor.unservable_units',
  },
  { key: 'reason', headerKey: 'inseason.droppedDoor.export.column.reason', kind: 'text', descriptionKey: 'inseason.columnGuide.droppedDoor.reason' },
  ARTICLE_NAME_REASON,
  STORE_NAME_REASON,
];

const named = (code: string, name: string | null | undefined, reason: string | null | undefined): Named => (
  { code, name: name ?? null, reason: name === null || name === undefined ? reason ?? null : null }
);

/** One dropped door as a row of {@link DROPPED_SHEET_COLUMNS}. The line's own names; none is read for it. */
export function droppedRow(line: ProposalSummary, translate: Translate): readonly ExportCell[] {
  const [storeName, articleName, storeReason, articleReason] = nameExportCells([
    named(line.store_key, line.store_name, line.store_name_absent_reason),
    named(line.article_key, line.article_name, line.article_name_absent_reason),
  ]) as [ExportCell, ExportCell, ExportCell, ExportCell];
  return [
    { kind: 'text', value: line.article_key },
    articleName,
    { kind: 'text', value: line.store_key },
    storeName,
    { kind: 'text', value: line.ship_week },
    { kind: 'integer', raw: line.requested_units },
    { kind: 'text', value: translate('inseason.droppedDoor.export.reasonValue') },
    articleReason,
    storeReason,
  ];
}

/**
 * `request` with the dropped doors' sheet beside its own. The request's table is unchanged (it is the
 * CSV); the workbook gets its sheet first and the dropped doors' second. With nothing dropped the
 * request is returned as it is: no second sheet, no empty one.
 */
export function withDroppedSheet(request: ExportRequest, dropped: readonly ProposalSummary[], translate: Translate): ExportRequest {
  if (dropped.length === 0) return request;
  const rows = dropped.map((line) => droppedRow(line, translate));
  const main: WorkbookSheet = { titleKey: request.titleKey, columns: request.columns, rows: request.rows };
  const apart: WorkbookSheet = { titleKey: DROPPED_SHEET_TITLE_KEY, columns: DROPPED_SHEET_COLUMNS, rows: () => rows };
  return {
    ...request,
    workbook: { ...(request.workbook?.titleKey === undefined ? {} : { titleKey: request.workbook.titleKey }), sheets: [main, apart] },
  };
}

/**
 * The request under another id and title, its workbook's first sheet re-titled with it — for a page
 * that exports the proposal file of its own level (a store, a department).
 */
export function retitled(request: ExportRequest, id: string, titleKey: string): ExportRequest {
  const next: ExportRequest = { ...request, id, titleKey };
  if (request.workbook === undefined) return next;
  return {
    ...next,
    workbook: { ...request.workbook, sheets: request.workbook.sheets.map((sheet, index) => (index === 0 ? { ...sheet, titleKey } : sheet)) },
  };
}

/** The exclusion's filters already on `request`, to carry when a page restates the perimeter. */
export function exclusionFiltersOf(request: ExportRequest): readonly ProvenanceFilter[] {
  const filters = request.provenance.filters;
  return Array.isArray(filters) ? filters.filter((filter: ProvenanceFilter) => filter.field === DROPPED_FILTER_FIELD) : [];
}

/**
 * Any file of proposal lines built OUTSIDE the proposal sheet — a table exported as drawn — with the
 * dropped doors' lines taken out of its table and put where the workbook keeps them: the request
 * carries the table of `served` lines, and this states the exclusion in its perimeter and in a cited
 * measure (the technical sheet reads it in words) and adds the second sheet. With nothing dropped the
 * request is returned as it is. A generic table has no `provenance_absent` column, so its CSV carries
 * no count: the screen's own count says what the files hold.
 */
export function leaveOutDroppedDoors(request: ExportRequest, dropped: readonly ProposalSummary[], translate: Translate): ExportRequest {
  if (dropped.length === 0) return request;
  const exclusion = exclusionOf(dropped);
  const filters = droppedFilters(exclusion);
  const cited = droppedMeasures(exclusion, translate).map((measure) => ({
    measure: measure.measure, definition: measure.definition, registerVersion: 'not_registered',
  }));
  const own = request.provenance.filters;
  return withDroppedSheet({
    ...request,
    provenance: {
      ...request.provenance,
      filters: Array.isArray(own) ? [...own, ...filters] : filters,
      measureDefinitions: [...request.provenance.measureDefinitions, ...cited],
    },
    perimeter: { ...request.perimeter, filters: [...request.perimeter.filters, ...filters] },
  }, dropped, translate);
}

// ── what the download buttons say the files hold ─────────────────────────────

/**
 * The buttons of a file that leaves the dropped doors out of its table say what it holds: «Scarica CSV:
 * 24.407 righe · 3.063 righe di negozi scartati escluse», «Scarica Excel: 24.407 righe + 3.063 righe di
 * negozi scartati nel foglio «Negozi scartati»» — not the row count of the table (which counts them),
 * for the file is the lines that propose something.
 */
export const DROPPED_DOWNLOAD_KEYS = {
  csv: 'inseason.droppedDoor.download.csv',
  xlsx: 'inseason.droppedDoor.download.xlsx',
  // What both CSVs hold: the CSV button's words after «Scarica CSV:», said by the link to the other dialect too (`docs/adr/9074`).
  holds: 'inseason.droppedDoor.download.holds',
} as const;

/**
 * {@link DROPPED_DOWNLOAD_KEYS} over rows whose proposal lines the file is made of, the figures counted
 * from them: for a table that holds the lines whole. A table that holds a part of them gives the
 * platform's counts instead (`droppedDownloadOf`).
 */
export function droppedDownloadOver<T>(linesOf: (rows: readonly T[]) => readonly ProposalSummary[]): DownloadLabelling<T> {
  return {
    keys: DROPPED_DOWNLOAD_KEYS,
    values: (rows) => {
      const { served, dropped } = splitDroppedDoors(linesOf(rows));
      return { served: served.length, dropped: dropped.length };
    },
  };
}

/** {@link DROPPED_DOWNLOAD_KEYS} with figures the platform counted (its `total_*_lines`, `matching_*_lines`, roll-up fields). */
export function droppedDownloadOf<T>(figures: () => { readonly served: number; readonly dropped: number }): DownloadLabelling<T> {
  return { keys: DROPPED_DOWNLOAD_KEYS, values: figures };
}
